import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Provider, useSelector } from 'react-redux';
import cn from 'classnames';
import { NEW_PATH_DELIMITER } from '@picsio/db/src/constants';
import { bool, func, string } from 'prop-types';

import store from '../../store';
import Checkbox from '../../UIComponents/checkbox';
import Tooltip from '../Tooltip';
import l10n from '../../shared/strings';
import SavedSearchGroupTree, { filterGroups } from '../SavedSearchGroupTree';

import s from './styles.module.scss';

/** Host `showDialog` name field — must not match inline tree `TreeItemEdit` inputs. */
export const SAVE_SEARCH_DIALOG_NAME_INPUT_TEST_ID = 'saveSearchDialogNameInput';

/**
 * Build the set of expanded paths leading to `targetPath` so the user
 * immediately sees their pre-selected (or contextually relevant) group
 * surfaced inside the tree.
 */
function expandedPathsTo(targetPath) {
  const next = new Set();
  if (!targetPath) return next;
  const parts = targetPath.split(NEW_PATH_DELIMITER).filter(Boolean);
  let acc = '';
  parts.forEach((p) => {
    acc = `${acc}${NEW_PATH_DELIMITER}${p}`;
    next.add(acc);
  });
  return next;
}

/**
 * Dialog body for the "Save this search" flow.
 *
 * A thin wrapper around <SavedSearchGroupTree> — adds the team/personal
 * scope toggle (share checkbox) and reports the active selection back to
 * the host showDialog via `onSelectionChange`.
 */
const SaveSearchDialogBody = ({
  defaultPath, defaultShared, canShare, onSelectionChange, onCreateGroup,
}) => {
  const tree = useSelector((state) => state.savedSearches.tree);

  const [shared, setShared] = useState(Boolean(defaultShared && canShare));
  const [path, setPath] = useState(defaultPath || '');
  const [pendingNewGroup, setPendingNewGroup] = useState(false);
  const [expanded, setExpanded] = useState(() => expandedPathsTo(defaultPath));

  const scope = shared ? tree?.teamSearches : tree?.personalSearches;
  const groups = useMemo(() => filterGroups(scope?.nodes || []), [scope]);

  useEffect(() => {
    onSelectionChange({ path, shared, pendingNewGroup });
  }, [path, shared, pendingNewGroup, onSelectionChange]);

  // Re-run the host dialog's name `validate` / `disableOk` when the user
  // changes scope, group, or the inline "+ New group" row — duplicate checks
  // and Ok disabled state must track the closure in `showSaveSearchDialog`.
  //
  // React dedupes `onChange` via the input's internal `_valueTracker`: on
  // every native `input`/`change` event it compares `node.value` against
  // the tracker's last-known value, and if they match the synthetic event
  // is silently dropped (see react-dom's inputValueTracking + ChangeEvent
  // plugin). We only mutate *external* selection state here (group/scope),
  // not the typed text, so a vanilla `dispatchEvent('input')` would never
  // reach the dialog's `validate` / `disableOk` callbacks — leaving stale
  // validation and a potentially-enabled OK button when the typed name
  // conflicts with a sibling in the newly-selected group.
  //
  // Force the tracker to disagree with the DOM value so React's dedupe
  // check fails open and onChange fires against the current input. The
  // tracker is a private React API but has been stable across 16/17/18
  // and is the canonical workaround used by react-testing-library,
  // Cypress, etc.
  //
  // Target only the host name field via data-testid — `children` render
  // before that input in DOM order, so `input[type="text"]` could match a
  // transient TreeItemEdit while it unmounts.
  useEffect(() => {
    const root = document.querySelector('.saveSearchHostDialog');
    const inputEl = root && root.querySelector(
      `input[type="text"][data-testid="${SAVE_SEARCH_DIALOG_NAME_INPUT_TEST_ID}"]`,
    );
    if (!inputEl) return;
    // eslint-disable-next-line no-underscore-dangle
    const tracker = inputEl._valueTracker;
    if (tracker && typeof tracker.setValue === 'function') {
      tracker.setValue(`${inputEl.value || ''}__pic_force_revalidate__`);
    }
    inputEl.dispatchEvent(new Event('input', { bubbles: true }));
  }, [path, shared, pendingNewGroup]);

  const handleToggleShare = useCallback((next) => {
    if (!canShare) return;
    setShared(next);
    // Group hierarchy differs between scopes — reset selection and any
    // half-open input rather than risk pointing at an invalid path.
    setPath('');
    setExpanded(new Set());
  }, [canShare]);

  const handleCreateGroup = useCallback(
    ({ parentPath, name }) => onCreateGroup({ parentPath, name, isShared: shared }),
    [onCreateGroup, shared],
  );

  const rootLabel = shared
    ? l10n.SAVEDSEARCHESTREE.textTeamSearches
    : l10n.SAVEDSEARCHESTREE.textSavedSearches;

  return (
    <div className={s.dialog}>
      <div className={s.row}>
        <div className={s.label}>
          {l10n.SEARCH.dialogSaveSearchSelectGroup}
        </div>
        {/*
          Re-key on scope so the inner picker re-mounts (resets its private
          "addingForPath" state) when the user flips the share checkbox.
        */}
        <SavedSearchGroupTree
          key={shared ? 'team' : 'personal'}
          groups={groups}
          fullScopeNodes={scope?.nodes || []}
          rootLabel={rootLabel}
          selectedPath={path}
          onSelectPath={setPath}
          expanded={expanded}
          setExpanded={setExpanded}
          onCreateGroup={handleCreateGroup}
          onAddingChange={setPendingNewGroup}
          testIdPrefix="saveSearchTree"
        />
        <If condition={pendingNewGroup}>
          <div className={s.pendingNewGroupHint} data-testid="saveSearchPendingNewGroupHint" role="status">
            {l10n.SEARCH.dialogSaveSearchPendingNewGroup}
          </div>
        </If>
      </div>

      <div className={cn(s.row, s.shareRow)}>
        <Tooltip
          content={canShare ? null : l10n.SEARCH.dialogSaveSearchShareNoPermission}
          placement="top"
        >
          <span className={s.shareWrapper}>
            <Checkbox
              value={shared}
              onChange={handleToggleShare}
              disabled={!canShare}
              label={l10n.SEARCH.dialogCreateSearchLabelShare}
              dataTestId="saveSearchShare"
            />
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

SaveSearchDialogBody.propTypes = {
  defaultPath: string,
  defaultShared: bool,
  canShare: bool.isRequired,
  onSelectionChange: func.isRequired,
  onCreateGroup: func.isRequired,
};

SaveSearchDialogBody.defaultProps = {
  defaultPath: '',
  defaultShared: false,
};

/**
 * The host showDialog mounts dialog content in a detached React root, so we
 * need our own Provider for `useSelector` to find the store. Mirrors the
 * pattern used by DropdownTreeWithStore.
 */
const SaveSearchDialog = (props) => (
  <Provider store={store}>
    <SaveSearchDialogBody {...props} />
  </Provider>
);

export default SaveSearchDialog;
